




























































import { Component, Ref, Vue } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientPartner, ClientPartnerPackage, ClientPartnerPackageList, PartnerEndpointGroup, PartnerPackageRecipient } from "@/data/models/ClientPartner";
import { createClientPartnerPackage } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { PackageRecipientChoice, isPartnerEmail, packageGroups, packageRecipient, partnerErrorKey } from "@/utils/ClientPartners";

const PARTNERS = "admin.clients.partners";

/**
 * Ticks the endpoint groups (starting from the newest package's, else the default) and the recipient
 * (a contact person of the system, or someone typed), then ERSTELLEN builds the package in IRES Cloud.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientPartnerPackageDialog extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  partner: ClientPartner | null = null;
  offeredGroups: PartnerEndpointGroup[] = [];
  startGroups: PartnerEndpointGroup[] = [];
  tickedGroups: PartnerEndpointGroup[] = [];
  apiVersion = "";
  recipientChoice: PackageRecipientChoice | null = null;
  typedName = "";
  typedEmail = "";

  private resolve: ((result: ClientPartnerPackage | null) => void) | null = null;

  get recipientItems() {
    const contacts = (this.partner?.contacts ?? []).map((contact, index) => ({
      value: index,
      label: `${contact.name} (${this.$t(`${PARTNERS}.contacts.roles.${contact.role}`).toString()})`,
    }));
    return [...contacts, { value: "OTHER", label: this.$t(`${PARTNERS}.packages.otherRecipient`).toString() }];
  }

  get recipient(): PartnerPackageRecipient | null {
    // Read every input first, so the computed tracks them all (ui-conventions.md).
    const typed = { name: this.typedName, email: this.typedEmail };
    return packageRecipient(this.recipientChoice, this.partner?.contacts, typed);
  }

  get typedEmailError(): string {
    const email = this.typedEmail.trim();
    return email !== "" && !isPartnerEmail(email) ? this.$t(`${PARTNERS}.contacts.invalid.email`).toString() : "";
  }

  get hasChanged(): boolean {
    const ticked = packageGroups(this.offeredGroups, this.tickedGroups).join(",");
    return ticked !== packageGroups(this.offeredGroups, this.startGroups).join(",") || this.recipientChoice !== null;
  }

  /**
   * @param clientId - the system's owner, whose page this is.
   * @param partner - the system, with its contact people.
   * @param list - what IRES Cloud offers: the groups, the ones to start from and the API version.
   */
  show(clientId: string, partner: ClientPartner, list: ClientPartnerPackageList): Promise<ClientPartnerPackage | null> {
    this.clientId = clientId;
    this.partner = partner;
    this.offeredGroups = list.groups;
    this.startGroups = packageGroups(list.groups, list.startGroups);
    this.tickedGroups = [...this.startGroups];
    this.apiVersion = list.apiVersion;
    this.recipientChoice = null;
    this.typedName = "";
    this.typedEmail = "";
    this.baseDialog.show({
      title: this.$t(`${PARTNERS}.packages.createTitle`, [partner.name]).toString(),
      positiveButtonText: this.$t(`${PARTNERS}.packages.createButton`).toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onCreate() {
    const recipient = this.recipient;
    if (!recipient || !this.partner) {
      return;
    }
    this.busy = true;
    try {
      const created = await createClientPartnerPackage(this.clientId, this.partner.id, packageGroups(this.offeredGroups, this.tickedGroups), recipient);
      SnackbarModule.showSuccess({ key: `${PARTNERS}.packages.created` });
      this.close(created);
    } catch (error) {
      SnackbarModule.showError({ key: partnerErrorKey(error) });
    } finally {
      this.busy = false;
    }
  }

  close(result: ClientPartnerPackage | null) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: ClientPartnerPackage | null) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
